/**
 * Slide-management action row — surfaces inside the inspector for the
 * currently-selected slide. Wraps the duplicate / delete buttons plus
 * the confirm dialog for delete.
 *
 * Decoupled from the rest of the inspector so each handler can live in
 * the parent (``DeckEditor``) where the deck-level mutation state and
 * the selection-tracking live.
 */

import { useState } from "react";

interface SlideActionsProps {
  slideNumber: number;
  slideTitle: string;
  onDuplicate(): void;
  onDelete(): void;
  /** Disable both actions while a previous request is still in flight. */
  busy?: boolean;
  /**
   * Disable delete only — used when the deck is already at 1 slide so
   * the deletion wouldn't leave any slides behind. Duplicate is still
   * fine because it always adds.
   */
  deleteDisabled?: boolean;
}

export function SlideActions({
  slideNumber,
  slideTitle,
  onDuplicate,
  onDelete,
  busy = false,
  deleteDisabled = false,
}: SlideActionsProps) {
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  return (
    <div style={rowStyle} data-testid="slide-actions">
      <button
        type="button"
        onClick={onDuplicate}
        disabled={busy}
        style={buttonStyle(busy)}
        data-testid="slide-action-duplicate"
      >
        Duplicate
      </button>
      <button
        type="button"
        onClick={() => setConfirmingDelete(true)}
        disabled={busy || deleteDisabled}
        style={{
          ...buttonStyle(busy || deleteDisabled),
          color: "var(--red-11)",
          borderColor: "var(--red-7)",
        }}
        title={
          deleteDisabled
            ? "Can't delete the only slide in the deck."
            : undefined
        }
        data-testid="slide-action-delete"
      >
        Delete
      </button>

      {confirmingDelete ? (
        <ConfirmDeleteDialog
          slideNumber={slideNumber}
          slideTitle={slideTitle}
          onCancel={() => setConfirmingDelete(false)}
          onConfirm={() => {
            setConfirmingDelete(false);
            onDelete();
          }}
        />
      ) : null}
    </div>
  );
}

function ConfirmDeleteDialog({
  slideNumber,
  slideTitle,
  onCancel,
  onConfirm,
}: {
  slideNumber: number;
  slideTitle: string;
  onCancel(): void;
  onConfirm(): void;
}) {
  return (
    <div
      style={backdropStyle}
      onClick={onCancel}
      data-testid="confirm-delete-backdrop"
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-label="Confirm slide deletion"
        style={dialogStyle}
        onClick={(e) => e.stopPropagation()}
        data-testid="confirm-delete-dialog"
      >
        <h3 style={{ margin: 0, fontSize: "1em" }}>Delete this slide?</h3>
        <p style={{ margin: "0.6em 0", color: "var(--gray-11)" }}>
          Slide {slideNumber}
          {slideTitle ? ` — "${slideTitle}"` : ""} will be removed and
          all following slides will shift up.
        </p>
        <p style={{ margin: "0.6em 0", fontSize: "0.85em", color: "var(--gray-10)" }}>
          Tombstoned on the server — the underlying node is preserved for
          audit. You'll need a database operation to bring it back.
        </p>
        <div style={dialogButtonsStyle}>
          <button
            type="button"
            onClick={onCancel}
            style={cancelButtonStyle}
            data-testid="confirm-delete-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            style={confirmButtonStyle}
            data-testid="confirm-delete-confirm"
            autoFocus
          >
            Delete slide
          </button>
        </div>
      </div>
    </div>
  );
}

const rowStyle: React.CSSProperties = {
  display: "flex",
  gap: "0.5rem",
  marginTop: "0.6rem",
};

function buttonStyle(disabled: boolean): React.CSSProperties {
  return {
    padding: "0.35rem 0.7rem",
    fontSize: "0.85em",
    border: "1px solid var(--gray-7)",
    background: "var(--gray-2)",
    color: "var(--gray-12)",
    borderRadius: 4,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
  };
}

const backdropStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0, 0, 0, 0.55)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1100,
};

const dialogStyle: React.CSSProperties = {
  background: "var(--gray-1)",
  color: "var(--gray-12)",
  border: "1px solid var(--gray-6)",
  borderRadius: 6,
  padding: "1rem 1.1rem",
  width: "min(420px, 92vw)",
  boxShadow: "0 24px 56px rgba(0, 0, 0, 0.45)",
};

const dialogButtonsStyle: React.CSSProperties = {
  display: "flex",
  gap: "0.5rem",
  justifyContent: "flex-end",
  marginTop: "0.5rem",
};

const cancelButtonStyle: React.CSSProperties = {
  padding: "0.4rem 0.9rem",
  background: "var(--gray-3)",
  color: "var(--gray-12)",
  border: "1px solid var(--gray-7)",
  borderRadius: 4,
  cursor: "pointer",
  fontSize: "0.9em",
};

const confirmButtonStyle: React.CSSProperties = {
  padding: "0.4rem 0.9rem",
  background: "var(--red-9)",
  color: "white",
  border: "1px solid var(--red-9)",
  borderRadius: 4,
  cursor: "pointer",
  fontWeight: 600,
  fontSize: "0.9em",
};
